import {Component, Inject, OnInit, Optional} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {DIALOG_DATA} from 'src/app/services/dialog.service';
import {DialogRef} from 'src/app/shared/ui-kits/dialog/dialog-ref';
import {Utils} from 'src/app/shared/utils/utils';
import {minNotInclude} from 'src/app/validators/custom.validators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DateTime} from 'luxon';
import {switchMap} from 'rxjs';
import {SalaryRequestStatusDictionary} from 'src/app/enums/salary-request-status-dictionary';
import {EmploymentForm} from 'src/app/interfaces/employee.interface';
import {
  ISalaryChanges,
  SalaryIncreaseFormGroup,
} from 'src/app/interfaces/salary-increase';
import {SalaryService} from 'src/app/services/accountant/salary.service';
import {SureModalService} from 'src/app/services/sure-modal/sure-modal.service';

@UntilDestroy()
@Component({
  selector: 'app-increase-salary-popup',
  templateUrl: './increase-salary-popup.component.html',
  styleUrls: ['./increase-salary-popup.component.scss'],
})
export class IncreaseSalaryPopupComponent implements OnInit {
  public form!: FormGroup; // increase salary form
  previousMonth = DateTime.now().minus({month: 1}).toJSDate();

  private hasApprovedIncreases = false;

  constructor(
    private dialogRef: DialogRef,
    private salaryService: SalaryService,
    private confirmService: SureModalService,
    @Optional()
    @Inject(DIALOG_DATA)
    public data: {
      increaseReasonItems: IDropdownObject[];
      employeeId: number;
      employmentForm: IDropdownObject;
    }
  ) {}

  ngOnInit(): void {
    this.form = this.buildForm();

    this.setInitialValues();
    this.subscribeOnIncreaseDateChanges();
  }

  buildForm(): FormGroup<SalaryIncreaseFormGroup> {
    return new FormGroup<SalaryIncreaseFormGroup>({
      employeeId: new FormControl<number | null>(this.data.employeeId),
      increaseDate: new FormControl<string | null>('', [Validators.required]),
      increaseReason: new FormControl<IDropdownObject | null>(null, [
        Validators.required,
      ]),
      salaryStavka: new FormControl<number | null>(null, [
        Validators.required,
        Validators.max(999999),
        Validators.min(0),
      ]),
      ratePerHour: new FormControl<number | null>(null, [
        Validators.required,
        Validators.max(999),
        Validators.min(0),
      ]),
      comment: new FormControl<string | null>('', [Validators.maxLength(400)]),
      constPart: new FormControl<number | null>(1, [
        Validators.required,
        Validators.max(1),
        minNotInclude(0),
      ]),
    });
  }

  setInitialValues() {
    if (this.isContractor) {
      this.salaryStavka?.setValue(0);
      this.ratePerHour.addValidators(Validators.min(0.1));
    } else {
      this.ratePerHour?.setValue(0);
      this.salaryStavka?.addValidators(Validators.min(0.1));
    }
  }

  get isContractor(): boolean {
    return this.data.employmentForm
      ? this.data.employmentForm.name !== EmploymentForm.Employee
      : false;
  }

  get increaseDate(): AbstractControl {
    return this.form.get('increaseDate')!;
  }

  get salaryStavka(): AbstractControl {
    return this.form.get('salaryStavka')!;
  }

  get ratePerHour(): AbstractControl {
    return this.form.get('ratePerHour')!;
  }

  get constPart(): AbstractControl {
    return this.form.get('constPart')!;
  }

  get comment(): AbstractControl {
    return this.form.get('comment')!;
  }

  get increaseReason(): AbstractControl {
    return this.form.get('increaseReason')!;
  }
  get total(): number {
    const HOURS = 168;
    return (
      Math.round(this.salaryStavka.value + HOURS * this.ratePerHour.value) ??
      null
    );
  }

  /**
   * This method sends data for increasing the salary
   */
  addIncreaseSalary() {
    if (this.form.invalid) {
      Utils.validateAllFormFields(this.form);
      return;
    }

    if (this.hasApprovedIncreases) {
      this.confirmService
        .openModal({
          text: 'The Salary Increase with such Active From Date was already Created and Approved. Do you want to update it?',
          acceptLabel: 'Yes',
          rejectLabel: 'No',
        })
        .pipe(untilDestroyed(this))
        .subscribe(res => this.dialogRef.close(res ? this.form.value : null));
    } else {
      this.dialogRef.close(this.form.value);
    }
  }

  private subscribeOnIncreaseDateChanges() {
    this.increaseDate.valueChanges
      .pipe(
        untilDestroyed(this),
        switchMap(date =>
          this.salaryService.getSalaryChanges(this.data.employeeId, {date})
        )
      )
      .subscribe(increases => {
        this.validateRequestAvailability(
          increases.salaries as ISalaryChanges[]
        );
      });
  }

  private validateRequestAvailability(salaries: ISalaryChanges[]) {
    const hasPendingIncreases = salaries.some(
      request => request.status === SalaryRequestStatusDictionary.Pending
    );

    if (hasPendingIncreases) {
      this.increaseDate.setErrors({hasPendingIncrease: true});
      return;
    }

    this.hasApprovedIncreases = salaries.some(
      request => request.status === SalaryRequestStatusDictionary.Approved
    );
  }
}
